/**
 * COMMENTS GROUPED BY ARTICLE, AS THE PROTOTYPE DRAWS THEM (design 15, the owner's live look at real
 * cabinets, 2026-10-01; `moonx-authors-prototype/MoonX App.dc.html`, `cabIsComments`): one card per
 * article, and in it each comment with the replies to it underneath.
 *
 * ★ A REGROUPING OF THE ROWS ALREADY LOADED, AND NOTHING MORE. `listCommentsOnMyArticles` serves one
 * flat, keyset-paged list, newest first (`created_at DESC, comment_key DESC` -
 * src/modules/engagement/comments-cabinet/cabinet-comment.repository.ts). Nothing here reads, counts
 * or infers anything the rows do not say, and no clock is read:
 *   - an article's card stands where its newest loaded row stood, so the cards keep the list's order;
 *   - inside a card the comments keep the list's order, newest first;
 *   - a reply sits under its comment when that comment is loaded, oldest reply first, as a
 *     conversation reads - the list's own order, reversed;
 *   - a reply whose comment is not loaded keeps its own place in the card, stepped in as a reply.
 *     Its comment is OLDER, so in a newest-first list it is on a later page, and "Load more" moves
 *     the reply under it. "Load more" never re-sorts anything else: new rows join their article's
 *     card, or open a card of their own below the ones already drawn.
 *
 * ★ AND NOTHING SAYS WHY A REPLY STANDS ALONE. Its comment may never arrive - a hidden comment is
 * absent from this list exactly as it is from the public thread (./CommentsRoute.tsx's header) - so
 * there is no "an earlier comment is not shown" line: it would leak the very fact the design removed.
 * The old flat list drew such a reply the same way, as a reply with nothing above it.
 */
import type { CabinetComment } from '../../features/cabinet/api';

/** One comment and what answers it, or a reply whose comment is not among the loaded rows. */
export interface CommentThread {
  readonly comment: CabinetComment;
  /** The loaded replies to `comment`, oldest first. Always empty when `comment` is itself a reply. */
  readonly replies: readonly CabinetComment[];
}

/** One article's card. */
export interface ArticleComments {
  readonly articleId: string;
  /** Omitted by the contract when no title resolves - never sent as null. */
  readonly articleTitle: string | undefined;
  /** The live page's path and a small picture, each only where there is one. */
  readonly articleWebPath: string | undefined;
  readonly articleCoverUrl: string | undefined;
  readonly threads: readonly CommentThread[];
  /** How many loaded rows belong to this article, replies included. */
  readonly count: number;
}

/** A reply names its comment; a comment on the article names none. */
export function parentOf(comment: CabinetComment): string | undefined {
  return comment.parentId === null || comment.parentId === undefined ? undefined : comment.parentId;
}

export function groupByArticle(items: readonly CabinetComment[]): ArticleComments[] {
  const loadedRoots = new Set(
    items.filter((item) => parentOf(item) === undefined).map((item) => item.commentId),
  );

  // The list is newest first; prepending each reply turns its comment's replies oldest first.
  const repliesTo = new Map<string, CabinetComment[]>();
  for (const item of items) {
    const parent = parentOf(item);
    if (parent !== undefined && loadedRoots.has(parent)) {
      const replies = repliesTo.get(parent) ?? [];
      replies.unshift(item);
      repliesTo.set(parent, replies);
    }
  }

  // A Map keeps insertion order: each article's card where its first (newest) row came.
  const cards = new Map<
    string,
    {
      articleTitle: string | undefined;
      articleWebPath: string | undefined;
      articleCoverUrl: string | undefined;
      threads: CommentThread[];
      count: number;
    }
  >();
  for (const item of items) {
    let card = cards.get(item.articleId);
    if (card === undefined) {
      card = {
        articleTitle: undefined,
        articleWebPath: undefined,
        articleCoverUrl: undefined,
        threads: [],
        count: 0,
      };
      cards.set(item.articleId, card);
    }
    card.count += 1;
    card.articleTitle ??= item.articleTitle;
    card.articleWebPath ??= item.articleWebPath;
    card.articleCoverUrl ??= item.articleCoverUrl;

    const parent = parentOf(item);
    if (parent !== undefined && loadedRoots.has(parent)) {
      continue; // drawn under its comment
    }
    card.threads.push({
      comment: item,
      replies: parent === undefined ? (repliesTo.get(item.commentId) ?? []) : [],
    });
  }

  return Array.from(cards, ([articleId, card]) => ({ articleId, ...card }));
}
